import type { ReactNode } from "react"
import { motion } from "framer-motion"

import { cn } from "@/lib/utils"

import { ColorBand, ColorBandInner } from "./ColorBand"
import { fadeUp } from "./marketing-motion"

type MarketingPageShellProps = {
  eyebrow?: string
  title: string
  description?: string
  children: ReactNode
  narrow?: boolean
  className?: string
  extra?: string
  /** Full-width colored hero band */
  heroVariant?: "default" | "primary" | "muted" | "lavender" | "sage"
  /** Optional actions under the hero description */
  actions?: ReactNode
  /** Skip the default content padding wrapper */
  bare?: boolean
}

const heroStyles = {
  default: "bg-white text-primary",
  primary: "bg-primary text-white",
  muted: "bg-[#f6f4ef] text-primary",
  lavender: "bg-[#be9afc] text-primary",
  sage: "bg-[#d2e4c4] text-primary",
} as const

export function MarketingPageShell({
  eyebrow,
  title,
  description,
  children,
  narrow = false,
  className,
  extra,
  heroVariant = "muted",
  actions,
  bare = false,
}: MarketingPageShellProps) {
  const onDark = heroVariant === "primary"
  const onTint = heroVariant === "lavender" || heroVariant === "sage"

  return (
    <div className="min-h-full">
      <ColorBand className={cn(heroStyles[heroVariant], "pt-28 sm:pt-32 lg:pt-36")}>
        <div
          className="pointer-events-none absolute -right-20 top-10 size-80 rounded-full bg-white/10 blur-3xl"
          aria-hidden
        />
        <div
          className={cn(
            "pointer-events-none absolute -left-16 bottom-0 size-72 rounded-full blur-3xl",
            onDark ? "bg-[#be9afc]/25" : "bg-primary/10",
          )}
          aria-hidden
        />
        {(onDark || onTint) && (
          <>
            <div
              className="pointer-events-none absolute left-[10%] top-30 size-7 rounded-full bg-[#be9afc]"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute right-[14%] bottom-16 size-9 rounded-full bg-primary"
              aria-hidden
            />
          </>
        )}
        <ColorBandInner>
          <motion.div {...fadeUp} className={cn(narrow && "max-w-3xl")}>
            {eyebrow ? (
              <p
                className={cn(
                  "font-heading text-sm font-semibold uppercase tracking-[0.18em]",
                  onDark ? "text-white/75" : "text-black/70",
                )}
              >
                {eyebrow}
              </p>
            ) : null}
            <h1
              className={cn(
                "font-display mt-4 max-w-4xl text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem] xl:text-[3.85rem]",
                onDark ? "text-white" : "text-black",
              )}
            >
              {title}
            </h1>
            {description ? (
              <p
                className={cn(
                  "mt-6 max-w-2xl text-base font-semibold leading-relaxed xl:text-lg",
                  onDark ? "text-white/90" : "text-black/75",
                )}
              >
                {description}
              </p>
            ) : null}
            {extra ? (
              <p
                className={cn(
                  "mt-3 max-w-2xl text-sm font-medium leading-relaxed",
                  onDark ? "text-white/75" : "text-black/60",
                )}
              >
                {extra}
              </p>
            ) : null}
            {actions ? <div className="mt-8 flex flex-wrap gap-3">{actions}</div> : null}
          </motion.div>
        </ColorBandInner>
      </ColorBand>

      {bare ? (
        children
      ) : (
        <div
          className={cn(
            "mx-auto px-4 py-14 sm:px-6 sm:py-20 lg:py-24",
            narrow ? "max-w-3xl" : "max-w-[96%] lg:max-w-[80%]",
            className,
          )}
        >
          {children}
        </div>
      )}
    </div>
  )
}
